// MUI
import { Grid } from "@mui/material";

//Layouts
import Title from "../../../common/Title";
import Button from "@/Layouts/components/Buttons/Button";

// Components
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";
import { useEffect, useState } from "react";
import {
	ICrossStoreEmailErrors,
	IInputCrossStoreEmailDistribution,
	TEmailDistributionUpdateParams,
} from "@/Settings/types/SystemSettings/emailDistribution";
import EmailDistributionLayout from "./EmailDistributionLayout";

import { ICrossStoreEmailDistribution } from "@/Settings/types/Common/model";
import formatEmailDistribution from "@/Settings/helpers/SystemSettings/emailDistribution/formatEmailDistribution";
import { validateCrossStoreEmailDistribution } from "@/Settings/helpers/SystemSettings/emailDistribution/validateEmailDistribution";

import { updateSystemSettings } from "@/Settings/api/SystemSettings/mutations";
import { useMutation } from "@tanstack/react-query";
import { errorLogger } from "@/Common/helpers/logger";

type TCrossStoreProps = {
	locationId: string;
	crossStoreEmailDistribution?: ICrossStoreEmailDistribution[];
};

const CrossStore = (props: TCrossStoreProps) => {
	const { locationId, crossStoreEmailDistribution } = props;

	const [emailDistribution, setEmailDistribution] = useState<IInputCrossStoreEmailDistribution[]>([]);
	const [errors, setErrors] = useState<ICrossStoreEmailErrors[]>([]);

	const { mutate: updateCrossStoreEmailDistributionData, isPending } = useMutation({
		mutationKey: ["systemSettings", locationId],
		mutationFn: async (input: IInputCrossStoreEmailDistribution[]) => {
			return updateSystemSettings(locationId, { crossStoreEmailDistribution: input }, "CROSS_STORE_EMAIL_DISTRIBUTION");
		},
		onError: (error) => errorLogger(error),
	});

	useEffect(() => {
		setEmailDistribution(formatEmailDistribution<ICrossStoreEmailDistribution>(crossStoreEmailDistribution ?? []));
		return () => {
			setEmailDistribution([]);
		};
	}, [crossStoreEmailDistribution]);

	const handleAddEmailDistribution = () => {
		const updated = [...emailDistribution];
		updated.push({
			toIndividuals: [],
			toNonEmployee: [],
			accountMappings: [
				{
					sourceGlAccount: "",
					destinationStore: {
						storeId: "",
						storeName: "",
					},
					destinationGlAccount: "",
					destinationSourceLabel: { label: "", value: "" },
					destinationControlType: { label: "", value: "", identifier: "" },
				},
			],
		});
		const validation = validateCrossStoreEmailDistribution(updated);
		setErrors(validation.errors);
		setEmailDistribution(updated);
	};

	const handleRemove = (index: number) => {
		const updated = [...emailDistribution];
		updated.splice(index, 1);
		setEmailDistribution(updated);
		update(updated);
	};
	const handleEmailDistribution = ({ index, key, value }: TEmailDistributionUpdateParams) => {
		const updated = [...emailDistribution];
		const updatedDistribution =
			key === "crossStoreAccountMappings" ? { ...updated[index], accountMappings: value } : { ...updated[index], [key]: value };
		updated[index] = updatedDistribution;
		setEmailDistribution(updated);
		update(updated);
	};
	const update = (updatedEmailDistribution: IInputCrossStoreEmailDistribution[] = []) => {
		// Duplicate validation: check last row of each distribution against all previous rows in all distributions
		let duplicateFound = false;
		const duplicateErrorIndexes: { distIdx: number; rowIdx: number }[] = [];
		// Build a set of all previous mappings for fast lookup
		const seen = new Set<string>();
		for (let i = 0; i < updatedEmailDistribution.length; i++) {
			const dist = updatedEmailDistribution[i];
			for (let j = 0; j < dist.accountMappings.length; j++) {
				const row = dist.accountMappings[j];
				if (!row.sourceGlAccount || !row.destinationStore?.storeId || !row.destinationGlAccount) continue;
				const key = `${row.sourceGlAccount}|${row.destinationStore.storeId}|${row.destinationGlAccount}`;
				if (seen.has(key)) {
					duplicateFound = true;
					duplicateErrorIndexes.push({ distIdx: i, rowIdx: j });
				} else {
					seen.add(key);
				}
			}
		}
		if (duplicateFound) {
			// Set errors for duplicate rows
			const newErrors = updatedEmailDistribution.map((dist, i) => ({
				...errors[i],
				accountMappings: dist.accountMappings.map((_, j) => {
					const isDup = duplicateErrorIndexes.some((idx) => idx.distIdx === i && idx.rowIdx === j);
					return {
						sourceGlAccount: isDup,
						destinationStore: isDup,
						destinationGlAccount: isDup,
						destinationStoreLabel: false,
						destinationControlType: false,
					};
				}),
			}));
			setErrors(newErrors);
			return;
		}
		// ...existing code...
		const { errors: updateErrors, isValid } = validateCrossStoreEmailDistribution(updatedEmailDistribution);
		setErrors(updateErrors);
		if (isValid) {
			updateCrossStoreEmailDistributionData(updatedEmailDistribution);
		}
	};
	return (
		<Grid container>
			<Grid item xs={12}>
				<Title title="Cross-Store Commissions Reconciliations / Email Distribution(s)" className="!mb-[24px]" />
			</Grid>
			<Grid item xs={12}>
				{emailDistribution.map((distribution, key) => {
					const keyIndex = `crossStore-Email-Distribution-${key}`;
					return (
						<EmailDistributionLayout
							locationId={locationId}
							key={keyIndex}
							index={key}
							distribution={distribution}
							errors={errors?.[key]}
							onRemove={handleRemove}
							onActionUpdate={handleEmailDistribution}
							isPending={isPending}
						/>
					);
				})}
			</Grid>
			<Button
				variant="contained"
				text="Add Email Distribution"
				classes="btn-regular-add btn-small-height btn-regular-text !mt-[24px]"
				minWidth={"180px"}
				endIcon={<AddCircleRounded />}
				onClick={handleAddEmailDistribution}
			/>
		</Grid>
	);
};

export default CrossStore;
